import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { getCallById } from "@/api/calls/calls.api";
import { Call } from "@/api/calls/calls.types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, AlertCircle, Info, MessageSquare, FileText, BarChart3, Headphones } from "lucide-react";
import { OverviewTab } from "./tabs/overview-tab";
import { TranscriptTab } from "./tabs/transcript-tab";
import { ConversationTab } from "./tabs/conversation-tab";
import { AnalysisTab } from "./tabs/analysis-tab";
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import { useAuth } from "@/context/AuthContext";
import { TRANSCRIPT_HIDDEN_ORG_IDS } from "@/config/restrictions";
import { CallDetailsLoadingSkeleton } from "./call-details-skeleton";
import { AudioPlayer } from "@/components/ui/audio-player";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { SiteHeader } from "@/components/site-header";

export default function CallDetailsPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const hideTranscripts = TRANSCRIPT_HIDDEN_ORG_IDS.includes(user?.organization_id || '');
    const [call, setCall] = useState<Call | null>(null);
    const [loading, setLoading] = useState(true);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const { error } = useEnhancedToast();

    // Add a cleanup effect when navigating away
    useEffect(() => {
        return () => {
            // Find any audio elements and pause them when unmounting
            const audioElements = document.querySelectorAll('audio');
            audioElements.forEach(audio => {
                audio.pause();
                audio.currentTime = 0;
                // Remove src to prevent any further loading/processing
                audio.removeAttribute('src');
                audio.load();
            });
        };
    }, []);

    const handleError = useCallback((err: Error | string) => {
        error({
            icon: AlertCircle,
            title: "Failed to Load Call",
            description: err instanceof Error ? err.message : err,
            duration: 4000,
        });
    }, []); // No dependencies needed as error is stable

    const copyToClipboard = (id: string) => {
        navigator.clipboard.writeText(id);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    useEffect(() => {
        if (!id) {
            navigate('/dashboard/calls');
            return;
        }

        setLoading(true);
        getCallById(id)
            .then(response => {
                if (response.error) {
                    throw new Error(response.error);
                }
                // console.log(response);
                setCall(response as unknown as Call);
            })
            .catch(error => {
                // console.error('Error fetching call:', error);
                handleError(error.message || "Failed to fetch call details");
                navigate('/dashboard/calls');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id, navigate, handleError]);

    if (loading) {
        return <CallDetailsLoadingSkeleton />;
    }

    if (!call) {
        return (
            <div className="container mx-auto p-6 space-y-4">
                <Button
                    variant="outline"
                    onClick={() => {
                        const queryParams = window.location.search;
                        navigate(`/dashboard/calls${queryParams}`);
                    }}
                    className="mb-4"
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Calls
                </Button>
                <div className="text-center py-12">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Call not found</h1>
                    <p className="text-muted-foreground mt-2">The call you're looking for doesn't exist or has been removed.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            <SiteHeader
                title="Call Details"
                subtitle="View comprehensive call information and recordings"
                backButton={
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            // Use browser's back navigation to preserve list state
                            navigate(-1);
                        }}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>
                }
            />


            <div className="flex-1 overflow-auto">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-4 py-6 md:px-8 md:py-8 space-y-6"
                >
                    {/* Audio Player */}
                    {(call.recordingUrl || call.stereoRecordingUrl) && (
                        <Card className="p-2 shadow-sm">
                            <AudioPlayer
                                url={(call.stereoRecordingUrl || call.recordingUrl) as string}
                                className="md:h-10"
                            />
                        </Card>
                    )}

                    <Tabs defaultValue="overview" className="w-full space-y-4">
                        <TabsList className="h-11">
                            <TabsTrigger value="overview" className="gap-2 px-4">
                                <Info className="h-4 w-4" />
                                Overview
                            </TabsTrigger>
                            {!hideTranscripts && (
                                <TabsTrigger value="conversation" className="gap-2 px-4">
                                    <MessageSquare className="h-4 w-4" />
                                    Conversation
                                </TabsTrigger>
                            )}
                            {!hideTranscripts && (
                                <TabsTrigger value="transcript" className="gap-2 px-4">
                                    <FileText className="h-4 w-4" />
                                    Transcript
                                </TabsTrigger>
                            )}
                            {call.analysis && (
                                <TabsTrigger value="analysis" className="gap-2 px-4">
                                    <BarChart3 className="h-4 w-4" />
                                    Analysis
                                </TabsTrigger>
                            )}
                        </TabsList>

                        <TabsContent value="overview" className="mt-0">
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}
                            >
                                <OverviewTab
                                    call={call}
                                    copiedId={copiedId}
                                    copyToClipboard={copyToClipboard}
                                />
                            </motion.div>
                        </TabsContent>
                        {!hideTranscripts && (
                            <TabsContent value="conversation" className="mt-0">
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <ConversationTab call={call} />
                                </motion.div>
                            </TabsContent>
                        )}
                        {!hideTranscripts && (
                            <TabsContent value="transcript" className="mt-0">
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <TranscriptTab call={call} />
                                </motion.div>
                            </TabsContent>
                        )}
                        {call.analysis && (
                            <TabsContent value="analysis" className="mt-0">
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <AnalysisTab call={call} />
                                </motion.div>
                            </TabsContent>
                        )}
                    </Tabs>
                </motion.div>
            </div>
        </div>
    );
}
